/**
 * rateLimitGate — a self-contained, module-level client brake that stops the
 * QueryClient from refetch-storming a rate-limited backend (MIDJEN-5CS).
 *
 * The QueryClient is created outside React (see `queryClient.ts`), so it can't
 * read a hook; this module is the bridge. Flow:
 *
 *  - `QueryCache.onError` calls `notifyRateLimitError()` when ANY query fails
 *    with a rate limit. That engages the brake.
 *  - The QueryClient reads `isRateLimited()` in its `refetchOnReconnect` and
 *    `retry` gates, so while the brake is engaged, queries do NOT
 *    refetch-on-reconnect and rate-limit errors are NOT retried.
 *  - The brake self-releases after `RATE_LIMIT_BRAKE_WINDOW_MS`; a still-active
 *    limit simply re-trips it on the next reconnect.
 *
 * This brake is SILENT. It gates the reconnect-refetch and retry paths for
 * every query — but NOT programmatic `invalidateQueries` (e.g. generation
 * polling) or mount-refetch, which fire independently. It deliberately does NOT
 * touch the generation cooldown UI
 * (`useRateLimitCooldown`): RLE counters are per-endpoint, so a throttle on a
 * background data read does not mean generation is throttled (measured ~97%
 * decoupled — MIDJEN-5CS). The user-facing Generate cooldown is tripped only by
 * real generation rate limits, via `useGeneration`.
 *//** How long the silent refetch-brake stays engaged after a rate-limit error. */export const RATE_LIMIT_BRAKE_WINDOW_MS=60_000;let rateLimited=false;export const rateLimitGate={/** True while the brake is engaged. Read by the QueryClient refetch/retry gates. */isRateLimited():boolean{return rateLimited;},/**
   * Engage the brake. Called by the QueryClient's `onError` when a query fails
   * with a rate limit.
   *
   * COALESCING (MIDJEN-5CS): `QueryCache.onError` fires once per failing query,
   * and a single reconnect fails every mounted query at once (the ingredient
   * fan-out alone is 3-6 queries). We engage only on the transition into
   * rate-limited and ignore the rest of the burst, so one transient blip arms a
   * single window rather than N stacked ones. The window then self-clears; if
   * the limit is still active, the next reconnect re-trips it.
   */notifyRateLimitError():void{if(rateLimited)return;// already braked — coalesce the burst
rateLimited=true;setTimeout(()=>{rateLimited=false;},RATE_LIMIT_BRAKE_WINDOW_MS);}};